import BaseModel, { type IResourceName } from 'volta/models/base-model';

import { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { TPlanningMethodology, TReorderStrategy } from './types/planning-settings';
import type { TAcquisitionCode } from './constants/acquisition-codes';
import type { TUserType } from './types/user';
import type { TEntityType } from './types/entity-types';

export const SearchEntryResourceName: IResourceName = {
  singular: 'searchEntry',
  plural: 'searchEntries'
};

type EntityType = Extract<
  TEntityType,
  | 'warehouses'
  | 'suppliers'
  | 'workshops'
  | 'skus'
  | 'users'
  | 'bufferProfiles'
  | 'dafProfiles'
  | 'aduProfiles'
>;
export interface ISearchEntryPayload {}
export interface CodeDescPayload extends ISearchEntryPayload {
  code: string;
  description?: string;
}
export interface PayloadWarehouse extends CodeDescPayload {}
export interface PayloadWorkshop extends CodeDescPayload {}
export interface PayloadSupplier extends CodeDescPayload {}
export interface PayloadBufferProfile extends CodeDescPayload {}
export interface PayloadAduProfile extends CodeDescPayload {}
export interface PayloadSku extends ISearchEntryPayload {
  partCode: string;
  description?: string;
  methodology?: TPlanningMethodology;
  reorderStrategy?: TReorderStrategy;
  acqCode: TAcquisitionCode;
  warehouseCode: string;
  isProtected: boolean;
  isActive: boolean;
}
export interface PayloadUser extends ISearchEntryPayload {
  login: string;
  email?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  userType: TUserType;
}

export interface PayloadDafProfile extends ISearchEntryPayload {
  name: string;
  description?: string;
}

export type Payload =
  | PayloadWarehouse
  | PayloadWorkshop
  | PayloadSupplier
  | PayloadBufferProfile
  | PayloadAduProfile
  | PayloadSku
  | PayloadUser
  | PayloadDafProfile;

const EntityRoutes: Record<EntityType, string> = {
  warehouses: 'protected.warehouse',
  suppliers: 'protected.supplier',
  workshops: 'protected.workshop',
  skus: 'protected.sku',
  users: 'protected.user',
  bufferProfiles: 'protected.buffer-profile',
  dafProfiles: 'protected.daf-profile',
  aduProfiles: 'protected.adu-profile'
};

export default class SearchEntry<P extends ISearchEntryPayload = Payload> extends BaseModel {
  @attr('string') entityId!: string;
  @attr('string') entityType!: EntityType;
  @attr('string') warehouseId?: string;
  @attr('string') term!: string;
  @attr() payload!: P;

  get entityRoute() {
    return EntityRoutes[this.entityType];
  }

  [Type] = 'search-entry' as const;
}
